import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NbCardModule, NbDialogService, NbIconModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux';
import { Campaign } from 'src/app/models/campaign';
import { Insight } from 'src/app/models/insight';
import { LineGraphData } from 'src/app/models/line-graph.models';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { ENVIRONMENT } from 'src/environments/environment';
import { WizardModalComponent } from './quick-start-wizard-modal/wizard-modal.component';
import { SharedModule } from '../../shared/shared.module';
import { StatsCampaignComponent } from '../campaigns/campaign-table/stats-campaign/stats-campaign.component';
import { ViewCampaignComponent } from '../campaigns/campaign-table/view-campaign/view-campaign.component';
import { CampaignState, LoadCampaigns, LoadQuickstartDeliveries, ReloadCampaign } from '../campaigns/redux';
import { ContentState } from '../content/redux';
import { LoadContent } from '../content/redux/content.actions';
import { StatsInsightComponent } from '../insights/insights-table/stats-insight/stats-insight.component';
import { ViewInsightComponent } from '../insights/insights-table/view-insight/view-insight.component';
import { InsightsState, LoadInsights, LoadInsightsInactive } from '../insights/redux';
import { LoadSurveys, SurveysState } from '../surveys/redux';

@Component({
  selector: 'app-quick-start',
  templateUrl: './quick-start.component.html',
  styleUrls: ['./quick-start.component.css'],
  imports: [NgIf, NbCardModule, NbIconModule, SharedModule]
})
export class QuickStartComponent implements OnInit, OnDestroy {
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);
  public activeInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsActive);
  public inactiveInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsInactive);
  public deliveries$: Observable<Record<string, any[]>> = inject(Store).select(CampaignState.quickstartDeliveries);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public username;
  public tableData;
  public insights = [];
  public showActions = [];
  public favorites;
  public isProduction: boolean = ENVIRONMENT.isProduction;
  public isTest: boolean = ENVIRONMENT.name === 'stage' || ENVIRONMENT.name === 'tkstage';
  public dates: Date[] = [];
  public deliveriesByCampaign: any = {};
  public campaignList: Campaign[] = [];
  public campaignIds: string[] = [];
  public graphData: LineGraphData = { labels: [], series: [] };
  public startDate: number;
  public endDate: number;

  constructor(
    private store: Store,
    private dialogService: NbDialogService
  ) {
    if (!this.store.selectSnapshot(InsightsState.insightsActive)) {
      this.store.dispatch(LoadInsights);
    }
    if (!this.store.selectSnapshot(InsightsState.insightsInactive)) {
      this.store.dispatch(LoadInsightsInactive);
    }
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
    if (!this.store.selectSnapshot(SurveysState.surveys)) {
      this.store.dispatch(LoadSurveys);
    }
    if (!this.store.selectSnapshot(CampaignState.campaign)) {
      this.store.dispatch(LoadCampaigns);
    }
  }

  private dateToYYYYMMDD(date: Date): number {
    return Number(date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate());
  }

  private formatReadableLabel(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private getCampaignList(data: any[]): any[] {
    const fourteenDaysAgoSeconds = Math.floor(Date.now() / 1000) - 14 * 24 * 60 * 60;

    const campaignList = data
      .filter((item) => item.created_by === this.username || this.favorites.includes(item.id))
      .filter((item) => item.last_run_time >= fourteenDaysAgoSeconds)
      .sort((a, b) => b.last_run_time - a.last_run_time)
      .slice(0, 10);

    return campaignList;
  }

  private getLast14Days(): Date[] {
    const dates: Date[] = [];
    const current = new Date();
    current.setDate(current.getDate() - 13);

    for (let i = 0; i < 14; i++) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  private getMissingList(campaignIds: string[]): string[] {
    const cachedDeliveries = this.store.selectSnapshot(CampaignState.quickstartDeliveries) ?? {};

    const missingIds: string[] = [];

    campaignIds.forEach((campaignId) => {
      if (!cachedDeliveries[campaignId]) {
        missingIds.push(campaignId);
      }
    });

    return missingIds;
  }

  private buildGraphDataFromDeliveries(
    campaignList: any[],
    deliveriesByCampaign: Record<string, any[]>,
    dates: Date[]
  ): LineGraphData {
    const labels = dates.map((date) => this.formatReadableLabel(date));
    const dateKeys = dates.map((date) => this.dateToYYYYMMDD(date));

    const series = campaignList.map((campaign) => {
      const campaignId = campaign.id;
      const deliveries = deliveriesByCampaign[campaignId] ?? [];
      const countByDate = new Map<number, number>();

      deliveries.forEach((item) => {
        countByDate.set(Math.trunc(item.delivery_date), item.count ?? 0);
      });

      const data: (number | null)[] = dateKeys.map((dateKey) => countByDate.get(dateKey) ?? 0);

      const hasStartDate = campaign.start_date !== null && campaign.start_date !== undefined;

      if (hasStartDate) {
        const startDateKey = this.dateToYYYYMMDD(new Date(campaign.start_date * 1000));
        dateKeys.forEach((dateKey, index) => {
          if (dateKey < startDateKey) {
            data[index] = null;
          }
        });
      } else {
        const firstNonZeroIndex = data.findIndex((value) => value !== 0);
        if (firstNonZeroIndex > 0) {
          for (let i = 0; i < firstNonZeroIndex; i++) {
            data[i] = null;
          }
        }
      }

      return { id: campaignId, label: campaignId, data };
    });

    const graphData: LineGraphData = { labels, series };

    return graphData;
  }

  public ngOnInit() {
    this.dates = this.getLast14Days();
    this.startDate = this.dateToYYYYMMDD(this.dates[0]);
    this.endDate = this.dateToYYYYMMDD(this.dates[this.dates.length - 1]);
    this.favorites = JSON.parse(localStorage.getItem('campaign_favorites')) || [];
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.activeInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.insights = this.insights.concat(data);
      }
    });
    this.inactiveInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.insights = this.insights.concat(data);
      }
    });
    this.campaign$.pipe(takeUntil(this.unsubscribe)).subscribe(async (data) => {
      if (data) {
        this.tableData = data
          .filter((item) => item.created_by === this.username || this.favorites.includes(item.id))
          .map((item) => ({
            ...item,
            insight: this.insights.find(({ id }) => id === item.trigger_id)
          }));
        this.campaignList = this.getCampaignList(data);
        this.campaignIds = this.campaignList.map((item) => item.id);

        if (this.campaignList.length > 0) {
          const missingIds = this.getMissingList(this.campaignIds);

          if (missingIds.length > 0) {
            this.store.dispatch(new LoadQuickstartDeliveries(missingIds, this.startDate, this.endDate, false));
          } else {
            const cachedDeliveries = this.store.selectSnapshot(CampaignState.quickstartDeliveries) ?? {};
            this.graphData = this.buildGraphDataFromDeliveries(this.campaignList, cachedDeliveries, this.dates);
          }
        }
      }
    });
    this.deliveries$.pipe(takeUntil(this.unsubscribe)).subscribe((deliveriesByCampaign) => {
      if (this.campaignList.length > 0 && deliveriesByCampaign) {
        this.graphData = this.buildGraphDataFromDeliveries(this.campaignList, deliveriesByCampaign, this.dates);
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public openModal(type: string) {
    this.dialogService.open(WizardModalComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        type
      }
    });
  }

  public async reloadDeliveries(): Promise<void> {
    this.store.dispatch(new LoadQuickstartDeliveries(this.campaignIds, this.startDate, this.endDate, true));
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadCampaign);
    }
    if ($event.action === ACTIONS.VIEW) {
      const dataType = $event.dataType;
      if (dataType === 'campaign') {
        const campaign: Campaign = $event.data;
        this.dialogService.open(ViewCampaignComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            campaign
          }
        });
      }
      if (dataType === 'insight') {
        const insight: Insight = $event.data;
        this.dialogService.open(ViewInsightComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            insight
          }
        });
      }
    }
    if ($event.action === ACTIONS.STATS) {
      const dataType = $event.dataType;
      if (dataType === 'campaign') {
        const campaign: Campaign = $event.data;
        this.dialogService.open(StatsCampaignComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            campaign
          }
        });
      }
      if (dataType === 'insight') {
        const insight: Insight = $event.data;
        this.dialogService.open(StatsInsightComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            insight
          }
        });
      }
    }
  }
}
